<app-dialog [visible]="visible" [fullscreenOnMobile]="true" class="max-w-[40rem]"
    (closed)="onDialogVisibleChange(false)">

    <ng-template #headerTemplate>
        <div class="text-lg font-bold">Transfer ownership ({{ userCount }} {{ userCount === 1 ? 'user' : 'user(s)'
            }} selected)</div>
    </ng-template>

    <ng-template #bodyTemplate>
        @if (transferState === 'initial') {
        <div class="flex flex-col gap-4">
            <!-- What all will transfer section -->
            <div class="flex flex-col gap-3">
                <h3 class="text-sm font-semibold text-gray-700">What all will transfer</h3>
                <div class="flex flex-row gap-3">
                    <div class="flex items-center gap-2">
                        <app-icon [type]="SVG.PROJECTS" [source]="'svg'" [size]="'sm'"
                            [class]="'text-gray-500'"></app-icon>
                        <span class="text-sm text-gray-600">Projects</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <app-icon [type]="SVG.DOCUMENTS" [source]="'svg'" [size]="'sm'"
                            [class]="'text-gray-500'"></app-icon>
                        <span class="text-sm text-gray-600">Documents</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <app-icon [type]="SVG.WORKSPACE" [source]="'svg'" [size]="'sm'"
                            [class]="'text-gray-500'"></app-icon>
                        <span class="text-sm text-gray-600">Workspace access</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <app-icon [type]="SVG.PRIVATE_SHARED" [source]="'svg'" [size]="'sm'"
                            [class]="'text-gray-500'"></app-icon>
                        <span class="text-sm text-gray-600">Private (unshared) documents</span>
                    </div>
                </div>
            </div>

            <!-- Files will be transferred to section -->
            <div class="flex flex-col gap-4 pt-2">
                <h3 class="text-sm font-semibold text-gray-700">Files will be transferred to</h3>
                @if (!_isDefaultTransferDisabled) {
                <!-- Checkbox for default transfer -->
                <div class="flex items-center gap-2">
                    <app-checkbox [(value)]="useDefaultTransfer" (onChange)="onCheckboxChange($event.checked)" [config]="{
              binary: true,
              label: 'Transfer files by default to ' + getAccountOwnerName(),
              name: 'defaultTransfer',
              disabled: _isDefaultTransferDisabled
            }">
                    </app-checkbox>
                </div>
                }
                <!-- User search input -->
                <div class="flex flex-col gap-2">
                    <p-iconField iconPosition="left">
                        <div class="flex w-full items-center space-x-2">
                            <div class="flex flex-grow rounded-base border border-gray-300">
                                <app-autocomplete #userAutocomplete [suggestions]="autocompleteSuggestions()"
                                    [(value)]="searchQuery" [config]="{
                                        placeholder: 'Type person name or email address',
                                        showEmptyMessage: false,
                                        dropdown: false,
                                        inputStyleClass: 'border-0 outline-none'
                                    }" [optionLabel]="'label'" (onComplete)="onSearch($event)"
                                    (onSelect)="onUserSelect($event.value)" [itemTemplate]="userItemTemplate">
                                </app-autocomplete>
                                <ng-template #userItemTemplate let-user>
                                    <div class="custom-item flex items-center p-2">
                                        <div class="user-avatar">
                                            <img [src]="user | userAvatar" class="w-8 h-8 rounded-full" />
                                        </div>
                                        <div class="ml-3">
                                            <span class="block font-semibold">{{ user | fullName }}</span>
                                            <span class="block text-sm text-gray-500">{{ user.email }}</span>
                                        </div>
                                    </div>
                                </ng-template>
                            </div>
                        </div>
                    </p-iconField>
                </div>

                <!-- Selected user display -->
                @if (selectedUser && !useDefaultTransfer) {
                <div class="flex items-center justify-between gap-3 rounded-base">
                    <div class="flex items-center gap-3 flex-1">
                        <p-avatar [image]="selectedUser | userAvatar" shape="circle"
                            [style]="{ width: '2.5rem', height: '2.5rem' }" />
                        <div class="flex flex-col flex-1">
                            <span class="text-sm font-medium">{{ selectedUser | fullName }}</span>
                            <span class="text-xs text-gray-500">{{ selectedUser.email }}</span>
                        </div>
                        <span class="text-xs  text-gray-700 font">
                            {{ getUserRoleLabel(selectedUser) }}
                        </span>
                    </div>
                    <button type="button" (click)="removeSelectedUser()"
                        class="p-2 hover:bg-gray-200 border border-gray-200 rounded-lg transition-colors"
                        aria-label="Remove user">
                        <i class="pi pi-trash text-red-600"></i>
                    </button>
                </div>
                }
            </div>
        </div>
        } @else if (transferState === 'transferring') {
        <!-- Transferring state -->
        <div class="flex flex-col items-center justify-center gap-4 py-8 px-6">
            @if (transferringAnimationData) {
            <app-lottie-animation [animationData]="transferringAnimationData" [loop]="true" [autoplay]="true"
                width="200px" height="200px">
            </app-lottie-animation>
            } @else {
            <div class="flex items-center justify-center">
                <i class="pi pi-spin pi-spinner text-4xl text-gray-500"></i>
            </div>
            }
            <h3 class="text-lg font-semibold text-gray-800">Transferring data</h3>
        </div>
        } @else if (transferState === 'success') {
        <!-- Success state -->
        <div class="flex flex-col items-center justify-center gap-4 py-8 px-6">
            @if (successAnimationData) {
            <app-lottie-animation [animationData]="successAnimationData" [loop]="false" [autoplay]="true" width="200px"
                height="200px">
            </app-lottie-animation>
            } @else {
            <div class="flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                <i class="pi pi-check text-2xl text-green-600"></i>
            </div>
            }
            <h3 class="text-lg font-semibold text-gray-800">Transfer successful</h3>
        </div>
        }
    </ng-template>

    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            @if (transferState === 'initial') {
            <app-button label="Don't transfer" (clicked)="cancel()" variant="outlined" severity="contrast" />
            <app-button label="Transfer ownership" (clicked)="confirmTransfer()" variant="filled" severity="primary"
                [disabled]="!getTransferEmail()" />
            } @else if (transferState === 'transferring') {
            <app-button label="Done" (clicked)="onDoneClick()" variant="filled" severity="primary" [disabled]="true" />
            } @else if (transferState === 'success') {
            <app-button label="Done" (clicked)="onDoneClick()" variant="filled" severity="primary" />
            }
        </div>
    </ng-template>
</app-dialog>